import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable from "../shared/DataTable";
import { useRoleNameById } from "../../hooks/useRole";
import { useUserByRole } from "../../hooks/useUser";
import { useCurrentUser } from "../../hooks/useAuth";
import UserSettings from "./UserSettings";
import { useDivisionNameById } from "../../hooks/useDivision";
import { useTeamNameById } from "../../hooks/useTeam";
import divisionsService from "../../services/divisionService";
import teamService from "../../services/teamService";
import roleService from "../../services/roleService";
import { ShimmerBadge, ShimmerTable } from "react-shimmer-effects";

// Separate components for cells that use hooks
const RoleCell = ({ roleId }) => {
  const { data: roleName } = useRoleNameById(roleId);
  return roleName || "";
};

const DivisionCell = ({ divisionId }) => {
  const { data: divisionName } = useDivisionNameById(divisionId);
  return divisionName || "";
};

const TeamCell = ({ teamId }) => {
  const { data: teamName } = useTeamNameById(teamId);
  return teamName || "";
};

const StatusCell = ({ status }) => (
  <span
    className={`px-3 py-1 rounded-full text-sm font-medium ${
      status ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
    }`}
  >
    {status ? "Active" : "Blocked"}
  </span>
);

const ActionsCell = ({ user }) => <UserSettings user={user} />;

export default function UsersTable({ filters }) {
  const user = useCurrentUser();
  const { data: users, isLoading: isUsersLoading } = useUserByRole({ user });
  const [enrichedUsers, setEnrichedUsers] = useState([]);

  useEffect(() => {
    const enrichUsers = async () => {
      // If users still loading from Firestore, wait
      if (!users) return;

      if (users.length === 0) {
        setEnrichedUsers([]);
        return;
      }
      try {
        const enriched = await Promise.all(
          users.map(async (user) => {
            const [divisionName, teamName, roleName] = await Promise.all([
              divisionsService.getNameById(user.division_id) || "",
              teamService.getNameById(user.team_id) || "",
              roleService.getNameById(user.role_id) || "",
            ]);

            return {
              ...user,
              division_name: divisionName,
              team_name: teamName,
              role_name: roleName,
            };
          })
        );

        setEnrichedUsers(enriched);
      } catch (err) {
        console.error("Error enriching users:", err);
      }
    };

    if (!isUsersLoading) enrichUsers();
  }, [users, isUsersLoading]);

  const filteredData = useMemo(() => {
    if (!enrichedUsers) return [];

    return enrichedUsers.filter((user) => {
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        const searchFields = [
          user.first_name,
          user.family_name,
          user.email_address,
          user.UPI_num,
          user.division_name,
          user.team_name,
          user.role_name,
        ];

        if (
          !searchFields.some((field) =>
            String(field || "")
              .toLowerCase()
              .includes(searchLower)
          )
        ) {
          return false;
        }
      }

      if (filters.selectedRole && user.role_id !== filters.selectedRole) {
        return false;
      }

      if (
        filters.selectedDivision &&
        user.division_id !== filters.selectedDivision
      ) {
        return false;
      }

      if (
        filters.selectedStatus !== "" &&
        user.status !== (filters.selectedStatus === "true")
      ) {
        return false;
      }

      return true;
    });
  }, [enrichedUsers, filters]);

  const columns = [
    {
      name: "First Name",
      selector: (row) => row.first_name,
      sortable: true,
    },
    {
      name: "Family Name",
      selector: (row) => row.family_name,
      sortable: true,
    },
    {
      name: "UPI",
      selector: (row) => row.UPI_num,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email_address,
      sortable: true,
    },
    {
      name: "Division",
      selector: (row) => row.division_id,
      sortable: true,
      cell: (row) => <DivisionCell divisionId={row.division_id} />,
    },
    {
      name: "Team",
      selector: (row) => row.team_id,
      sortable: true,
      cell: (row) => <TeamCell teamId={row.team_id} />,
    },
    {
      name: "Role",
      selector: (row) => row.role_id,
      sortable: true,
      cell: (row) => <RoleCell roleId={row.role_id} />,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => <StatusCell status={row.status} />,
    },
    {
      name: "Actions",
      cell: (row) => <ActionsCell user={row} />,
    },
  ];

  if (
    isUsersLoading || // Firestore data still loading
    !users || // Users not fetched yet
    (users.length > 0 && !enrichedUsers.length) // Enrichment not done
  ) {
    return (
      <div className="relative min-h-[400px] bg-white rounded-lg shadow-md">
        <div className="flex justify-end mt-8 mr-10">
          <ShimmerBadge width={250} />
        </div>
        <ShimmerTable row={8} col={8} gap={20} />
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={filteredData}
      searchFields={[
        "first_name",
        "family_name",
        "email",
        "UPI_num",
        "division_name",
        "team_name",
        "role_name",
      ]}
      searchPlaceholder="Search Users..."
      paginationPerPage={10}
    />
  );
}
